import "./products.css";

const PRODUCT_API_URL = "https://api.ayesearch.com/api/crawl/products/";

export interface ShoppingProduct {
  merchant: string;
  title: string;
  description: string;
  price: string;
  old_price: string;
  image_url: string;
  thumbnail_url: string;
  product_url: string;
  rating: number | null;
  fetched_at: string;
}

interface ProductCatalogResponse {
  category: string;
  count: number;
  limit: number;
  merchants: string[];
  products: ShoppingProduct[];
}

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function merchantLabel(value: string): string {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "jumia") return "Jumia";
  if (normalized === "konga") return "Konga";
  if (normalized === "amazon") return "Amazon";
  return normalized
    ? normalized.charAt(0).toUpperCase() + normalized.slice(1)
    : "Store";
}

function renderProductCard(product: ShoppingProduct): string {
  const imageUrl = product.thumbnail_url || product.image_url;
  const description = product.description || product.title;
  const rating =
    typeof product.rating === "number" && Number.isFinite(product.rating)
      ? `${product.rating.toFixed(1)} / 5`
      : "";

  return `
    <a
      class="ayesearch-product-card"
      href="${escapeHtml(product.product_url)}"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Open ${escapeHtml(product.title)} on ${escapeHtml(
        merchantLabel(product.merchant),
      )}"
    >
      <div class="ayesearch-product-image-wrap">
        ${
          imageUrl
            ? `<img class="ayesearch-product-image" src="${escapeHtml(
                imageUrl,
              )}" alt="" loading="lazy" referrerpolicy="no-referrer" />`
            : `<div class="ayesearch-product-image-placeholder">Table tennis</div>`
        }
      </div>
      <div class="ayesearch-product-copy">
        <div class="ayesearch-product-merchant">${escapeHtml(
          merchantLabel(product.merchant),
        )}</div>
        <strong class="ayesearch-product-title">${escapeHtml(product.title)}</strong>
        <p class="ayesearch-product-description">${escapeHtml(description)}</p>
        <div class="ayesearch-product-meta">
          <span class="ayesearch-product-price">${escapeHtml(
            product.price || "View price",
          )}</span>
          ${rating ? `<span class="ayesearch-product-rating">${escapeHtml(rating)}</span>` : ""}
        </div>
      </div>
    </a>
  `;
}

function initializeImageFallbacks(container: HTMLElement): void {
  container
    .querySelectorAll<HTMLImageElement>(".ayesearch-product-image")
    .forEach((image) => {
      image.addEventListener("error", () => {
        const wrap = image.closest(".ayesearch-product-image-wrap");
        if (!wrap) return;
        wrap.innerHTML = `<div class="ayesearch-product-image-placeholder">Table tennis</div>`;
      });
    });
}

export async function renderProductsRow(
  container: HTMLElement,
  category: string,
): Promise<void> {
  container.innerHTML = `
    <section class="ayesearch-products-shell" aria-label="Table tennis products">
      <div class="ayesearch-products-heading">
        <div>
          <div class="ayesearch-products-kicker">SHOPPING</div>
          <h2>Table tennis products</h2>
        </div>
        <span>Jumia · Konga · Amazon</span>
      </div>
      <div class="ayesearch-products-loading">Loading cached products…</div>
    </section>
  `;

  try {
    const url = new URL(PRODUCT_API_URL);
    url.searchParams.set("category", category);
    url.searchParams.set("limit", "12");

    const response = await fetch(url.toString(), {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    });

    if (!response.ok) {
      throw new Error(`Products API returned HTTP ${response.status}`);
    }

    const data = (await response.json()) as ProductCatalogResponse;
    const products = Array.isArray(data.products) ? data.products.slice(0, 12) : [];

    if (!products.length) {
      container.innerHTML = "";
      return;
    }

    container.innerHTML = `
      <section class="ayesearch-products-shell" aria-label="Table tennis products">
        <div class="ayesearch-products-heading">
          <div>
            <div class="ayesearch-products-kicker">SHOPPING</div>
            <h2>Table tennis products</h2>
          </div>
          <span>${escapeHtml(
            [...new Set(products.map((product) => merchantLabel(product.merchant)))].join(
              " · ",
            ),
          )}</span>
        </div>
        <div class="ayesearch-products-row">
          ${products.map(renderProductCard).join("")}
        </div>
      </section>
    `;

    initializeImageFallbacks(container);
  } catch (error) {
    console.error("AyeSearch product row failed:", error);
    // Shopping is additive. If the cache/API is unavailable, keep the SportsCard
    // and ordinary web results intact instead of replacing them with an error.
    container.innerHTML = "";
  }
}
